import { DialogContentText } from "@material-ui/core";
import AssignProductDialog from "@saleor/banners/components/AssignProductDialog";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignMallDialog from "@saleor/components/AssignMallDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { createPaginationState } from "@saleor/hooks/usePaginator";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useCouponCollectionSearch from "@saleor/searches/useCouponCollectionSearch";
import useCouponSearch from "@saleor/searches/useCouponSearch";
import useMallQuery from "@saleor/searches/useMallQuery";
import useProductSearch from "@saleor/searches/useProductSearch";
import useSaleSearch from "@saleor/searches/useSaleSearch";
import useVoucherSearch from "@saleor/searches/useVoucherSearch";
import {
  CouponCollectionSortField,
  CouponCollectionStatusEnum,
  CouponStatusEnum,
  OrderDirection,
  SmopBannerTypeEnum,
  SmopStatusEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import AssignBrandDialog from "../components/AssignBrandDialog";
import AssignCategoryDialog from "../components/AssignCategoryDialog";
import AssignCollectionDialog from "../components/AssignCollectionDialog";
import AssignCouponCollectionDialog from "../components/AssignCouponCollectionDialog/AssignCouponCollectionDialog";
import AssignCouponDialog from "../components/AssignCouponDialog/AssignCouponDialog";
import BannerDetailsPage, {
  BannerDetailsPageTab
} from "../components/BannerDetailsPage";
import {
  TypedBannerDelete,
  TypedBannerLinkUpdate,
  TypedBannerMallsAdd,
  TypedBannerMallsRemove,
  TypedBannerUpdate,
  useBannerCreateDeeplink
} from "../mutations";
import { useBannerDetailsQuery } from "../queries";
import { BannerDelete } from "../types/BannerDelete";
import { BannerLinkUpdate } from "../types/BannerLinkUpdate";
import { BannerMallsAdd } from "../types/BannerMallsAdd";
import { BannerMallsRemove } from "../types/BannerMallsRemove";
import { BannerUpdate } from "../types/BannerUpdate";
import {
  bannerListUrl,
  bannerUrl,
  BannerUrlDialog,
  BannerUrlQueryParams
} from "../urls";

interface BannerDetailsProps {
  id: string;
  params: BannerUrlQueryParams;
  lastTableNavigation: string;
}

export const BannerDetails: React.FC<BannerDetailsProps> = ({
  id,
  params,
  lastTableNavigation
}) => {
  const navigate = useNavigator();
  const intl = useIntl();
  const notify = useNotifier();
  const MALLS_LIMIT = 100;
  const [currentBrand, setCurrentBrand] = React.useState(null);
  const [currentLevel, setCurrentLevel] = React.useState(null);
  const [couponStatusQuery, setCouponStatusQuery] = useState<
    CouponStatusEnum[]
  >([CouponStatusEnum.ACTIVE, CouponStatusEnum.SCHEDULED]);

  const isCouponCollectionAssignMutationRef = React.useRef(false);
  const paginationState = createPaginationState(MALLS_LIMIT, params);

  const { data, loading, refetch } = useBannerDetailsQuery({
    displayLoader: true,
    variables: {
      id,
      ...paginationState
    }
  });

  const { search: searchSales, result: sales } = useSaleSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const { search: searchVouchers, result: vouchers } = useVoucherSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const brandInitialSearchData = {
    after: null,
    first: 50,
    query: "",
    status: SmopStatusEnum.ACT
  };

  const mallsInitialSearchData = {
    after: null,
    first: 100,
    query: "",
    statuses: [SmopStatusEnum.ACT]
  };

  const {
    loadMore: loadMoreMalls,
    search: searchMalls,
    result: malls
  } = useMallQuery({
    variables: mallsInitialSearchData
  });

  const assignedMalls = maybe(() => data.banner.malls.edges, []).map(
    edge => edge.node.id
  );

  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsQueryData
  } = useBrandSearch({
    variables: {
      ...brandInitialSearchData,
      mallsId: assignedMalls
    }
  });

  const sellerBrandChoices = maybe(
    () => brandsQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      brands: currentBrand ? [currentBrand] : [],
      malls: assignedMalls
    }
  });

  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    skip: currentLevel === null,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      level: currentLevel
    }
  });

  const bannerMalls = data?.banner?.malls?.edges.map(mall => mall.node.id);

  const {
    loadMore: loadMoreCoupons,
    search: searchCoupons,
    result: searchCouponOpts
  } = useCouponSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      ...(bannerMalls?.length === 0
        ? { isAppliedToAllMalls: true }
        : { participatingMalls: bannerMalls }),
      status: couponStatusQuery
    }
  });

  const {
    loadMore: loadMoreCouponCollections,
    search: searchCouponCollections,
    result: searchCouponCollectionOpts
  } = useCouponCollectionSearch({
    variables: {
      first: 100,
      query: "",
      sortBy: {
        direction: OrderDirection.DESC,
        field: CouponCollectionSortField.UPDATED_AT
      },
      status: [
        CouponCollectionStatusEnum.ACTIVE,
        CouponCollectionStatusEnum.INACTIVE,
        CouponCollectionStatusEnum.SCHEDULED
      ]
    }
  });

  const changeTab = (tab: BannerDetailsPageTab) => {
    navigate(
      bannerUrl(id, {
        activeTab: tab
      }),
      false,
      false,
      false
    );
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    BannerUrlDialog,
    BannerUrlQueryParams
  >(navigate, params => bannerUrl(id, params), params);

  const handleBannerUpdate = (data: BannerUpdate) => {
    if (data.bannerUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully updated banner"
        })
      });
    }
    refetch();
  };

  const handleBannerDelete = (data: BannerDelete) => {
    if (data.bannerDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully deleted banner"
        })
      });
      navigate(bannerListUrl());
    }
  };

  const handleBannerLinkUpdate = (data: BannerLinkUpdate) => {
    const shouldShowCommunityAssignedSuccess =
      isCouponCollectionAssignMutationRef.current;
    isCouponCollectionAssignMutationRef.current = false;

    if (data.bannerAnnouncementLinkUpdate.errors.length === 0) {
      closeModal();
      notify({
        status: "success",
        text: shouldShowCommunityAssignedSuccess
          ? intl.formatMessage({
              defaultMessage: "Community Assigned Successfully"
            })
          : intl.formatMessage({
              defaultMessage: "Successfully updated banner"
            })
      });
    }
    refetch();
  };

  const handleBannerAddMalls = (data: BannerMallsAdd) => {
    if (data.bannerAddMalls.bannerErrors.length === 0) {
      closeModal();
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully updated banner"
        })
      });
    }
    refetch();
  };

  const handleBannerRemoveMalls = (data: BannerMallsRemove) => {
    if (data.bannerRemoveMalls.bannerErrors.length === 0) {
      closeModal();
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Successfully updated banner"
        })
      });
    }
  };

  const [createDeeplink, createDeeplinkOpts] = useBannerCreateDeeplink({
    onCompleted: data => {
      const errors = data?.bannerGenerateDynamicLink?.bannerErrors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Banner Updated",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, somethin went wrong...",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const sendNullImage = (image: string) => (!image ? { imageUrl: null } : {});
  const handleAssignMalls = (malls, bannerMallAdd) => {
    bannerMallAdd({
      variables: {
        ...paginationState,
        id,
        input: {
          isAppliedToAllMalls: false,
          malls
        }
      }
    });
  };
  return (
    <TypedBannerLinkUpdate onCompleted={handleBannerLinkUpdate}>
      {(bannerLinkUpdate, bannerLinkUpdateOpts) => (
        <TypedBannerMallsRemove onCompleted={handleBannerRemoveMalls}>
          {(bannerMallRemove, bannerMallsRemoveOpts) => (
            <TypedBannerMallsAdd onCompleted={handleBannerAddMalls}>
              {(bannerMallAdd, bannerMallsAddOpts) => (
                <TypedBannerUpdate onCompleted={handleBannerUpdate}>
                  {(bannerUpdate, bannerUpdateOpts) => (
                    <TypedBannerDelete onCompleted={handleBannerDelete}>
                      {(bannerDelete, bannerBulkDeleteOpts) => (
                        <>
                          <WindowTitle
                            title={intl.formatMessage(sectionNames.banners)}
                          />
                          <BannerDetailsPage
                            lastTabLocation={lastTableNavigation}
                            activeTab={params.activeTab}
                            data={data && data.banner}
                            disabled={
                              loading ||
                              bannerUpdateOpts.loading ||
                              bannerBulkDeleteOpts.loading ||
                              bannerMallsAddOpts.loading ||
                              bannerMallsRemoveOpts.loading ||
                              bannerLinkUpdateOpts.loading ||
                              createDeeplinkOpts.loading
                            }
                            errors={bannerUpdateOpts.data?.bannerUpdate.errors}
                            fetchMoreMalls={{
                              hasMore: maybe(
                                () => malls.data.search.pageInfo.hasNextPage
                              ),
                              loading: malls.loading,
                              onFetchMore: loadMoreMalls
                            }}
                            fetchMalls={searchMalls}
                            fetchSales={searchSales}
                            fetchVouchers={searchVouchers}
                            malls={maybe(
                              () =>
                                malls.data.search.edges.map(edge => edge.node),
                              []
                            )}
                            sales={maybe(
                              () =>
                                sales.data.search.edges.map(edge => edge.node),
                              []
                            )}
                            saveButtonBarState={bannerUpdateOpts.status}
                            id={id}
                            onBack={() => navigate(bannerListUrl())}
                            onBrandAssign={() => openModal("assign-brand")}
                            onBrandUnassign={() =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    brandId: null
                                  }
                                }
                              })
                            }
                            onCategoryAssign={() =>
                              openModal("assign-category")
                            }
                            onCategoryUnassign={() =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    categoryId: null
                                  }
                                }
                              })
                            }
                            onCollectionAssign={() =>
                              openModal("assign-collection")
                            }
                            onCollectionUnassign={() =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    collectionId: null
                                  }
                                }
                              })
                            }
                            onDelete={() => openModal("delete")}
                            onGenerateDeeplink={id =>
                              createDeeplink({ variables: { id } })
                            }
                            onProductAssign={() => openModal("assign-product")}
                            onProductUnassign={() =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    productId: null
                                  }
                                }
                              })
                            }
                            onMallAssign={() => openModal("assign-mall")}
                            onMallUnassign={malls => {
                              let isAllMalls = false;
                              if (data.banner.malls.totalCount === 1) {
                                isAllMalls = true;
                              }
                              bannerMallRemove({
                                variables: {
                                  ...paginationState,
                                  id,
                                  input: {
                                    isAppliedToAllMalls: isAllMalls,
                                    malls: [malls]
                                  }
                                }
                              });
                              refetch();
                            }}
                            onSubmit={formData => {
                              const additionalParams = {
                                descriptionJson: JSON.stringify(
                                  formData.description
                                ),
                                dynamicLinkId:
                                  data.banner.brand ||
                                  data.banner.category ||
                                  data.banner.collection ||
                                  data.banner.product
                                    ? null
                                    : formData.dynamicLinkId,
                                endDate: changeTimeZone(
                                  formData.endDate,
                                  formData.endTime
                                ),
                                mall:
                                  formData.mall === "all"
                                    ? null
                                    : formData.mall,
                                name: formData.name,
                                sale: null,
                                startDate: changeTimeZone(
                                  formData.startDate,
                                  formData.startTime
                                ),
                                verticals: formData.verticals || [],
                                voucher: null
                              };
                              if (formData.type === SmopBannerTypeEnum.SALE) {
                                additionalParams.sale = formData.saleId;
                                additionalParams.name = formData.saleName;
                                additionalParams.startDate = null;
                                additionalParams.endDate = null;
                                additionalParams.descriptionJson = null;
                              } else if (
                                formData.type === SmopBannerTypeEnum.VOUCHER
                              ) {
                                additionalParams.voucher = formData.voucherId;
                                additionalParams.name = formData.voucherName;
                                additionalParams.startDate = null;
                                additionalParams.endDate = null;
                                additionalParams.descriptionJson = null;
                              }
                              bannerUpdate({
                                variables: {
                                  id,
                                  input: {
                                    ...additionalParams,
                                    ...sendNullImage(formData.image),
                                    isSplashBanner: formData.isSplashBanner,
                                    status: formData.status
                                  }
                                }
                              });
                            }}
                            onTabClick={changeTab}
                            vouchers={maybe(
                              () =>
                                vouchers.data.search.edges.map(
                                  edge => edge.node
                                ),
                              []
                            )}
                            onCouponAssign={() => openModal("assign-coupon")}
                            onCouponUnassign={() =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    couponId: null
                                  }
                                }
                              })
                            }
                            onCouponCollectionAssign={() =>
                              openModal("assign-coupon-collection")
                            }
                            onCouponCollectionUnassign={() => {
                              isCouponCollectionAssignMutationRef.current = false;
                              return bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    couponCollectionId: null
                                  }
                                }
                              });
                            }}
                          />
                          <AssignMallDialog
                            confirmButtonState={bannerUpdateOpts.status}
                            field="banner"
                            malls={maybe(() =>
                              malls.data.search.edges
                                .map(edge => edge.node)
                                .filter(suggestedMall => suggestedMall.id)
                            )}
                            onClose={() => navigate(bannerUrl(id))}
                            onConfirm={formData =>
                              handleAssignMalls(formData, bannerMallAdd)
                            }
                            onFetch={searchMalls}
                            open={params.action === "assign-mall"}
                            initial={maybe(() => data.banner?.malls.edges)}
                            fetchMoreMalls={{
                              hasMore: maybe(
                                () =>
                                  malls &&
                                  malls.data &&
                                  malls.data.search.pageInfo.hasNextPage
                              ),
                              loading: malls.loading,
                              onFetchMore: loadMoreMalls
                            }}
                            loading={malls.loading}
                            hasMore={malls?.data?.search?.pageInfo?.hasNextPage}
                            onFetchMore={loadMoreMalls}
                          />
                          <AssignProductDialog
                            brands={sellerBrandChoices}
                            fetchBrands={query => searchBrands(query)}
                            fetchMoreBrands={{
                              hasMore: maybe(
                                () =>
                                  brandsQueryData &&
                                  brandsQueryData.data &&
                                  brandsQueryData.data.search.pageInfo
                                    .hasNextPage
                              ),
                              loading: brandsQueryData.loading,
                              onFetchMore: loadMoreBrand
                            }}
                            confirmButtonState={bannerUpdateOpts.status}
                            hasMore={
                              searchProductsOpts.data?.search?.pageInfo
                                .hasNextPage
                            }
                            open={params.action === "assign-product"}
                            onFetch={searchProducts}
                            onFetchMore={loadMoreProducts}
                            onSelectBrand={value => setCurrentBrand(value)}
                            loading={searchProductsOpts.loading}
                            onClose={closeModal}
                            onSubmit={products =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    productId: products[0].id
                                  }
                                }
                              })
                            }
                            products={maybe(() =>
                              searchProductsOpts.data.search.edges
                                .map(edge => edge.node)
                                .filter(suggestedProduct => suggestedProduct.id)
                            )}
                          />
                          <AssignBrandDialog
                            brands={maybe(() =>
                              brandsQueryData.data.search.edges
                                .map(edge => edge.node)
                                .filter(suggestedProduct => suggestedProduct.id)
                            )}
                            confirmButtonState={bannerUpdateOpts.status}
                            hasMore={
                              brandsQueryData.data?.search?.pageInfo.hasNextPage
                            }
                            loading={brandsQueryData.loading}
                            onFetch={searchBrands}
                            onFetchMore={loadMoreBrand}
                            onClose={closeModal}
                            onSubmit={brands =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    brandId: brands[0].id
                                  }
                                }
                              })
                            }
                            open={params.action === "assign-brand"}
                          />
                          <AssignCollectionDialog
                            collections={maybe(() =>
                              searchCollectionsOpts.data.search.edges
                                .map(edge => edge.node)
                                .filter(suggestedProduct => suggestedProduct.id)
                            )}
                            confirmButtonState={bannerUpdateOpts.status}
                            hasMore={
                              searchCollectionsOpts.data?.search?.pageInfo
                                .hasNextPage
                            }
                            loading={searchCollectionsOpts.loading}
                            onFetch={searchCollections}
                            onFetchMore={loadMoreCollections}
                            onClose={closeModal}
                            onSubmit={collections =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    collectionId: collections[0].id
                                  }
                                }
                              })
                            }
                            open={params.action === "assign-collection"}
                          />
                          <AssignCategoryDialog
                            confirmButtonState={bannerUpdateOpts.status}
                            hasMore={
                              searchCategoriesOpts.data?.search?.pageInfo
                                .hasNextPage
                            }
                            open={params.action === "assign-category"}
                            onFetch={searchCategories}
                            onFetchMore={loadMoreCategories}
                            onSelectLevel={value => setCurrentLevel(value)}
                            loading={searchCategoriesOpts.loading}
                            onClose={closeModal}
                            onSubmit={category =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    categoryId: category[0].id
                                  }
                                }
                              })
                            }
                            categories={maybe(() =>
                              searchCategoriesOpts.data.search.edges
                                .map(edge => edge.node)
                                .filter(suggestedProduct => suggestedProduct.id)
                            )}
                          />
                          <AssignCouponCollectionDialog
                            open={params.action === "assign-coupon-collection"}
                            onClose={closeModal}
                            loading={searchCouponCollectionOpts.loading}
                            hasMore={maybe(
                              () =>
                                searchCouponCollectionOpts.data.search.pageInfo
                                  .hasNextPage,
                              false
                            )}
                            couponCollections={maybe(
                              () =>
                                searchCouponCollectionOpts.data.search.edges.map(
                                  edge => edge.node
                                ),
                              []
                            )}
                            onFetch={searchCouponCollections}
                            onFetchMore={loadMoreCouponCollections}
                            onSubmit={couponCollectionId => {
                              isCouponCollectionAssignMutationRef.current = true;
                              return bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    couponCollectionId
                                  }
                                }
                              });
                            }}
                          />
                          <AssignCouponDialog
                            open={params.action === "assign-coupon"}
                            onClose={closeModal}
                            coupons={maybe(() =>
                              searchCouponOpts.data.search.edges.map(
                                edge => edge.node
                              )
                            )}
                            couponStatusQuery={couponStatusQuery}
                            setCouponStatusQuery={setCouponStatusQuery}
                            onFetch={searchCoupons}
                            // fetchMoreCoupons={
                            //   {
                            //     hasMore: maybe(() => (
                            //       searchCouponOpts.data?.search?.pageInfo
                            //       .hasNextPage
                            //     ),
                            //     loading: searchCouponOpts.loading,
                            //     onFetchMore: loadMoreCoupons
                            //   }
                            // }
                            fetchMoreCoupons={{
                              hasMore: maybe(
                                () =>
                                  searchCouponOpts?.data?.search?.pageInfo
                                    ?.hasNextPage
                              ),
                              loading: searchCouponOpts?.loading,
                              onFetchMore: loadMoreCoupons
                            }}
                            onSubmit={couponID =>
                              bannerLinkUpdate({
                                variables: {
                                  id,
                                  input: {
                                    couponId: couponID
                                  }
                                }
                              })
                            }
                          />
                          <ActionDialog
                            confirmButtonState={bannerBulkDeleteOpts.status}
                            onClose={closeModal}
                            onConfirm={() =>
                              bannerDelete({ variables: { id } })
                            }
                            open={params.action === "delete"}
                            title={intl.formatMessage({
                              defaultMessage: "Delete banner",
                              description: "dialog header"
                            })}
                            confirmButtonLabel="Delete"
                            variant="delete"
                          >
                            <DialogContentText>
                              <FormattedMessage
                                defaultMessage="Are you sure you want to delete this banner?"
                                description="dialog content"
                              />
                            </DialogContentText>
                          </ActionDialog>
                        </>
                      )}
                    </TypedBannerDelete>
                  )}
                </TypedBannerUpdate>
              )}
            </TypedBannerMallsAdd>
          )}
        </TypedBannerMallsRemove>
      )}
    </TypedBannerLinkUpdate>
  );
};

export default BannerDetails;
